import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { bulletColumns } from '../lib/bullet.js';
import { withFormats } from '../lib/table.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  BULLET_SORTS,
  bulletExportRows,
  bulletGeometry,
  bulletModel,
  bulletSummary,
} from '../lib/bullet.js';
import { thresholdScale } from '../lib/heatmap.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called, singular and plural. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

/** The orders the lib knows, in its order, with what the buttons call them. */
const SORT_LABELS = { allocation: 'Allocation', over: 'Hours over', label: 'Name' };
const SORTS = BULLET_SORTS.map((key) => ({ key, label: SORT_LABELS[key] }));

/**
 * The bullet rows (V-3, PRD 10.3; Phase 24): the heatmap's selected week read as hours. One row
 * per entity, its threshold bands as the track's background, allocated hours as the bar, the
 * high-confidence hours as the solid inner bar, and capacity as the tick — with the percentage,
 * the hours and the hours over or free beside it.
 *
 * It draws no data of its own: the page hands it the same `GET /forecast/heatmap` response the
 * matrix above is drawn from (`data`), so a row here and the cell above it are the same figure
 * of the same run, which is what the phase gate checks. Every number comes out of
 * `lib/bullet.js`; the component formats and positions, and computes nothing else.
 *
 * Encoding (PRD 7.3 and 7.4): the status vocabulary, as the heatmap. The track's four bands are
 * the run's thresholds applied to that row's capacity, in the status tints; the bar is the
 * band's own reserved colour with the band's icon in the label, so a bar and the cell above it
 * wear the same colour; the part of the bar that is not high confidence is hatched, which is the
 * confidence vocabulary (solid, hatch, dots) rather than a second hue. A row with no capacity
 * has no bands and no tick: its hours are drawn on the hatched no-capacity track.
 *
 * Rows are keyed, so changing the week or the sort moves the bars rather than repainting them.
 */
class BulletRows extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the selected week start; null lets the card pick the peak
    _week: { state: true },
    _sort: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._week = null;
    this._sort = 'allocation';
    this._tableOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 12px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      select,
      .segmented button {
        font: inherit;
        font-size: 13px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 4px 8px;
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* ---- the rows ---- */
      .chart {
        display: grid;
        grid-template-columns: minmax(120px, 210px) minmax(180px, 1fr) minmax(190px, auto);
        align-items: center;
        gap: 2px 14px;
      }
      .axis {
        display: contents;
      }
      .axis .ruler {
        position: relative;
        height: 18px;
        border-bottom: 1px solid var(--grid);
      }
      .axis .t {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        font-size: 10.5px;
        color: var(--tick-ink);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      .axis .unit,
      .axis .caption {
        font-size: 10.5px;
        color: var(--tick-ink);
      }
      .row {
        display: contents;
      }
      .name {
        display: flex;
        flex-direction: column;
        gap: 1px;
        padding: 5px 0;
        font-size: 13px;
        color: var(--ink-strong);
        min-width: 0;
      }
      .name button {
        font: inherit;
        font-weight: 600;
        text-align: left;
        color: inherit;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .name button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      .name .small {
        font-size: 11px;
        font-weight: 400;
        color: var(--ink-muted);
      }
      .track {
        position: relative;
        height: 26px;
        background: var(--surface-2);
        border: 1px solid var(--border-soft);
        border-radius: 3px;
        overflow: hidden;
      }
      .track:focus-visible {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }
      .band {
        position: absolute;
        top: 0;
        bottom: 0;
      }
      .band[data-band='underload'] {
        background: var(--status-underload-tint);
      }
      .band[data-band='healthy'] {
        background: var(--status-healthy-tint);
      }
      .band[data-band='caution'] {
        background: var(--status-caution-tint);
      }
      .band[data-band='overloaded'] {
        background: var(--status-overloaded-tint);
      }
      .grid {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 1px;
        background: var(--grid);
      }
      /* The allocated bar: the band's colour, hatched where the work is not high confidence. */
      .bar {
        position: absolute;
        left: 0;
        top: 5px;
        bottom: 5px;
        border-radius: 2px;
      }
      .bar[data-band='underload'] {
        background: var(--status-underload);
      }
      .bar[data-band='healthy'] {
        background: var(--status-healthy);
      }
      .bar[data-band='caution'] {
        background: var(--status-caution);
      }
      .bar[data-band='overloaded'] {
        background: var(--status-overloaded);
      }
      .bar[data-band='none'] {
        background: repeating-linear-gradient(
          45deg,
          var(--surface-2),
          var(--surface-2) 3px,
          var(--border-soft) 3px,
          var(--border-soft) 6px
        );
        border: 1px solid var(--border);
      }
      .bar.mixed::after {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 3px,
          var(--hatch-ink) 3px,
          var(--hatch-ink) 5px
        );
        border-radius: 2px;
      }
      /* The inner bar: the high-confidence hours, solid and inset, as a bullet chart draws them. */
      .inner {
        position: absolute;
        left: 0;
        top: 10px;
        bottom: 10px;
        border-radius: 1px;
        background: var(--ink-strong);
      }
      /* A bar past the end of the axis: marked, so a clipped row is never read as a full one. */
      .clip {
        position: absolute;
        right: 1px;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        padding: 0 2px;
        font-weight: 700;
        color: var(--ink-strong);
        background: var(--surface);
        border-left: 1px solid var(--border);
      }
      .tick {
        position: absolute;
        top: 1px;
        bottom: 1px;
        width: 2px;
        margin-left: -1px;
        background: var(--ink-strong);
        border-radius: 1px;
      }
      .figures {
        display: flex;
        align-items: baseline;
        gap: 10px;
        font-size: 12.5px;
        font-variant-numeric: tabular-nums;
        color: var(--ink-muted);
        white-space: nowrap;
      }
      .figures .pct {
        font-size: 13.5px;
        font-weight: 700;
        color: var(--ink-strong);
        min-width: 4.6em;
      }
      .figures .h {
        color: var(--ink);
      }
      .figures .delta {
        font-weight: 600;
      }
      .figures .delta[data-kind='over'] {
        color: var(--status-overloaded);
      }
      .foot {
        display: grid;
        gap: 4px;
        margin: 12px 0 0;
        padding: 10px 0 0;
        border-top: 1px solid var(--border);
      }
      .foot-row {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 14px;
      }
      .foot dt {
        font-size: 13px;
        font-weight: 700;
        color: var(--ink-strong);
      }
      .foot dt .small {
        font-size: 11px;
        font-weight: 400;
        color: var(--ink-muted);
        margin-left: 8px;
      }
      .foot dd {
        margin: 0 0 0 auto;
      }
      .row[data-selected='true'] .name button {
        color: var(--link);
        text-decoration: underline;
      }
      .row[data-selected='true'] .track {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }

      /* ---- chrome ---- */
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        margin-top: 12px;
        padding: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        width: 26px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .legend .sw.inner {
        width: 14px;
        height: 8px;
        background: var(--ink-strong);
        border: 0;
      }
      .legend .sw.tick {
        width: 2px;
        height: 14px;
        background: var(--ink-strong);
        border: 0;
      }
      .legend .sw.mixed {
        background: repeating-linear-gradient(
          45deg,
          var(--surface-2),
          var(--surface-2) 3px,
          var(--border) 3px,
          var(--border) 5px
        );
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
        .chart {
          grid-template-columns: 1fr;
          gap: 0;
        }
        .name {
          padding-bottom: 0;
        }
        .figures {
          padding: 2px 0 10px;
          flex-wrap: wrap;
          white-space: normal;
        }
        .axis .ruler {
          display: none;
        }
      }
    `,
  ];

  /**
   * The host may own the week (the heatmap page does, so the matrix can mark the same column).
   * Whenever it says so — including when it clears it — that is the week; otherwise the card
   * keeps what it was last asked for and `bulletModel` falls back to the window's peak.
   */
  willUpdate(changed) {
    if (changed.has('week')) this._week = this.week;
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    return this.data ? bulletModel(this.data, { week: this._week, sort: this._sort }) : null;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  /**
   * The bars: one per entity. The company total and the role-only line are sums of those rows
   * rather than peers of them (the company is 30 times the largest person in a week), so they
   * are stated as figures under the chart instead of as bars that would flatten the rest.
   */
  _drawn(model) {
    return model.rows.map((r) => ({ row: r, kind: 'entity' }));
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  /** The week is the page's to own (the matrix highlights it too), so say so and keep a copy. */
  _selectWeek(weekStart) {
    this._week = weekStart || null;
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: this._week },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _toggleEntity(row) {
    const { filters } = this.appState;
    const dimension = this.data?.dimension;
    if (!dimension || dimension === 'company' || row.key === 'unassigned') return;
    const already = filters.dimension === dimension && filters.entity === row.key;
    setFilters({ dimension, entity: already ? null : row.key });
  }

  _isSelected(row) {
    const { filters } = this.appState;
    return filters.dimension === this.data?.dimension && filters.entity === row.key;
  }

  _showTooltip(event, row) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const state = STATUS_STATES.find((s) => s.key === row.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(row.allocated_hours) },
      { label: '  High confidence', value: exact(row.high_confidence_hours) },
      { label: '  Rest', value: exact(row.other_hours) },
      { label: 'Capacity', value: exact(row.capacity_hours) },
      row.over_hours > 0
        ? { label: 'Over capacity', value: exact(row.over_hours) }
        : { label: 'Headroom', value: exact(row.headroom_hours) },
    ];
    const split = [
      ['Committed (explicit)', row.cell.explicit_hours],
      ['Committed (inferred)', row.cell.inferred_hours],
      ['Recurring', row.cell.recurring_hours],
      ['Non-project', row.cell.nonproject_hours],
      ['Pipeline', row.cell.pipeline_hours],
    ].filter(([, v]) => v > 0);
    for (const [label, v] of split) rows.push({ label: `  ${label}`, value: exact(v) });
    const facts = [
      row.utilization === null ? 'No capacity' : `${percent(row.utilization)} allocated`,
      state ? `${state.icon} ${state.label}` : null,
      row.cell.people ? `${count(row.cell.people)} people` : null,
      row.cell.projects ? `${count(row.cell.projects)} projects` : null,
      row.cell.opportunities ? `${count(row.cell.opportunities)} deals` : null,
    ];
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `${row.label} · week of ${longDate(row.week_start)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hideTooltip() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  /** What a row states in words: the same sentence the figures column prints, for a screen reader. */
  _rowLabel(row, clipped = false) {
    const state = STATUS_STATES.find((s) => s.key === row.band);
    if (row.utilization === null)
      return `${row.label}: no capacity, ${formatHours(row.allocated_hours)} allocated${
        clipped ? ', past the end of the scale' : ''
      }`;
    const delta =
      row.over_hours > 0
        ? `${formatHours(row.over_hours)} over`
        : `${formatHours(row.headroom_hours)} free`;
    return `${row.label}: ${percent(row.utilization)} allocated, ${state?.label ?? ''}, ${formatHours(
      row.allocated_hours,
    )} of ${formatHours(row.capacity_hours)}, ${delta}, of which ${formatHours(
      row.high_confidence_hours,
    )} high confidence`;
  }

  _bullet(model, row) {
    const g = bulletGeometry(row, model.scale.max, this.data.thresholds);
    const band = row.band ?? 'none';
    const mixed = row.other_hours > 0 && row.has_capacity;
    return html`
      <div
        class="track"
        tabindex="0"
        role="img"
        aria-label=${this._rowLabel(row, g.clipped)}
        @pointermove=${(e) => this._showTooltip(e, row)}
        @pointerleave=${() => this._hideTooltip()}
        @focus=${(e) => this._showTooltip(e, row)}
        @blur=${() => this._hideTooltip()}
      >
        ${g.bands.map(
          (b) =>
            html`<span
              class="band"
              data-band=${b.key}
              style=${`left:${b.from}%;width:${b.to - b.from}%`}
            ></span>`,
        )}
        ${model.scale.values
          .filter((v) => v > 0 && v < model.scale.max)
          .map(
            (v) =>
              html`<span
                class="grid"
                style=${`left:${(v / model.scale.max) * 100}%`}
                aria-hidden="true"
              ></span>`,
          )}
        <span
          class=${`bar ${mixed ? 'mixed' : ''}`}
          data-band=${band}
          style=${`width:${g.bar}%`}
        ></span>
        ${
          row.has_capacity && row.high_confidence_hours > 0
            ? html`<span class="inner" style=${`width:${g.inner}%`}></span>`
            : nothing
        }
        ${g.tick === null ? nothing : html`<span class="tick" style=${`left:${g.tick}%`}></span>`}
        ${g.clipped ? html`<span class="clip" aria-hidden="true">›</span>` : nothing}
      </div>
    `;
  }

  _figures(row) {
    const state = STATUS_STATES.find((s) => s.key === row.band);
    return html`
      <div class="figures">
        <span class="pct"
          >${state ? html`<span aria-hidden="true">${state.icon}</span> ` : nothing}${
            row.utilization === null ? '—' : percent(row.utilization)
          }</span
        >
        <span class="h"
          >${formatHours(row.allocated_hours)} /
          ${row.has_capacity ? formatHours(row.capacity_hours) : 'no capacity'}</span
        >
        ${
          row.has_capacity
            ? html`<span class="delta" data-kind=${row.over_hours > 0 ? 'over' : 'free'}
                >${
                  row.over_hours > 0
                    ? `+${formatHours(row.over_hours)}`
                    : `${formatHours(row.headroom_hours)} free`
                }</span
              >`
            : nothing
        }
      </div>
    `;
  }

  _chart(model) {
    const dimension = this.data.dimension;
    const drawn = this._drawn(model);
    return html`
      <div class="chart">
        <div class="axis">
          <div class="unit">Hours</div>
          <div class="ruler" aria-hidden="true">
            ${model.scale.values.map((v, i) => {
              // The first and last labels sit inside the ruler rather than half off each end.
              const at = (v / model.scale.max) * 100;
              const shift =
                i === 0 ? 'none' : i === model.scale.values.length - 1 ? 'translateX(-100%)' : null;
              return html`<span
                class="t"
                style=${`left:${at}%${shift ? `;transform:${shift}` : ''}`}
                >${v}</span
              >`;
            })}
          </div>
          <div class="caption">Allocated / capacity</div>
        </div>
        ${repeat(
          drawn,
          (d) => d.row.key,
          (d) => html`
            <div
              class="row"
              data-key=${d.row.key}
              data-band=${d.row.band ?? 'none'}
              data-selected=${this._isSelected(d.row) ? 'true' : 'false'}
            >
              <div class="name">
                ${
                  dimension === 'company'
                    ? html`<span>${d.row.label}</span>`
                    : html`<button
                        type="button"
                        title=${
                          this._isSelected(d.row)
                            ? 'Clear the entity filter'
                            : `Filter the views to ${d.row.label}`
                        }
                        @click=${() => this._toggleEntity(d.row)}
                      >
                        ${d.row.label}
                      </button>`
                }
                ${
                  dimension === 'individual' && d.row.entity?.department
                    ? html`<span class="small">${d.row.entity.department}</span>`
                    : nothing
                }
              </div>
              ${this._bullet(model, d.row)} ${this._figures(d.row)}
            </div>
          `,
        )}
      </div>
      ${this._foot(model)}
    `;
  }

  /**
   * The role-only line and the company total, in figures. They are the sums of the bars above,
   * not bars themselves: on one scale the company would be the only thing visible.
   */
  _foot(model) {
    if (!model.foot.length) return nothing;
    return html`
      <dl class="foot">
        ${model.foot.map(
          (row) => html`
            <div class="foot-row" data-key=${row.key} data-band=${row.band ?? 'none'}>
              <dt>
                ${row.label}
                ${
                  row.key === 'unassigned'
                    ? html`<span class="small">hours, not a ratio: no person, no capacity</span>`
                    : html`<span class="small">the sum of every row, withheld ones included</span>`
                }
              </dt>
              <dd>${this._figures(row)}</dd>
            </div>
          `,
        )}
      </dl>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls(model) {
    const peakWeekStart = model.weeks.reduce(
      (best, w) =>
        w.utilization !== null && (best === null || w.utilization > best.utilization) ? w : best,
      null,
    );
    return html`
      <div class="controls">
        <span>Week</span>
        <select
          aria-label="The week the bullet rows are drawn for"
          @change=${(e) => this._selectWeek(e.target.value)}
        >
          ${model.weeks.map(
            (w) => html`
              <option value=${w.week_start} ?selected=${w.week_start === model.week.week_start}>
                Week of
                ${weekLabel(w.week_start)}${
                  w.utilization === null ? '' : ` · ${percent(w.utilization)}`
                }${peakWeekStart?.week_start === w.week_start ? ' · peak' : ''}
              </option>
            `,
          )}
        </select>
        ${
          peakWeekStart && peakWeekStart.week_start !== model.week.week_start
            ? html`<button
                class="link"
                type="button"
                @click=${() => this._selectWeek(peakWeekStart.week_start)}
              >
                Jump to the peak week (${weekLabel(peakWeekStart.week_start)},
                ${percent(peakWeekStart.utilization)})
              </button>`
            : nothing
        }
        <div class="segmented" role="group" aria-label="Row order">
          <span>Sort</span>
          <span class="group">
            ${SORTS.map(
              (s) =>
                html`<button
                  type="button"
                  aria-pressed=${this._sort === s.key ? 'true' : 'false'}
                  @click=${() => (this._sort = s.key)}
                >
                  ${s.label}
                </button>`,
            )}
          </span>
        </div>
      </div>
    `;
  }

  _legend() {
    const scale = thresholdScale(this.data?.thresholds);
    return html`
      <ul class="legend" aria-label="What the marks mean">
        <li>
          <span class="sw inner" aria-hidden="true"></span>
          <span>Inner bar: high confidence</span>
        </li>
        <li>
          <span class="sw mixed" aria-hidden="true"></span>
          <span>Hatched: inferred, analog or pipeline</span>
        </li>
        <li>
          <span class="sw tick" aria-hidden="true"></span>
          <span>Tick: capacity</span>
        </li>
        ${scale.map(
          (s) => html`
            <li>
              <span class="sw" style=${`background: var(${s.tint});`} aria-hidden="true"></span>
              <span>${s.label} <span class="range">(${s.range} of capacity)</span></span>
            </li>
          `,
        )}
      </ul>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const rowsOf =
      this.data.dimension === 'company'
        ? 'the company'
        : `${count(model.rows.length)} ${this._nouns[1]}`;
    return `${rowsOf} in the week of ${weekLabel(model.week.week_start)}${
      model.week.is_peak ? ' (the window’s peak)' : ''
    } · hours, on one scale · pipeline ${this.data.pipeline_mode}`;
  }

  _kpis(model) {
    const s = this._summary(model);
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${count(s.rows_over)}</div>
          <div class="l">of ${count(s.rows)} ${this._nouns[1]} over capacity</div>
        </div>
        <div class="kpi">
          <div class="n">${formatHours(s.hours_over)}</div>
          <div class="l">over, added up</div>
        </div>
        <div class="kpi">
          <div class="n">
            ${model.total.utilization === null ? '—' : percent(model.total.utilization)}
          </div>
          <div class="l">company that week</div>
        </div>
      </div>
    `;
  }

  _summary(model) {
    return bulletSummary(model);
  }

  _notes(model) {
    const { filters } = this.appState;
    const s = this._summary(model);
    const notes = [];
    if (s.no_capacity > 0)
      notes.push(
        html`<p class="note" data-note="no-capacity">
          ${count(s.no_capacity)} ${s.no_capacity === 1 ? 'row has' : 'rows have'} no capacity at
          all, carrying ${formatHours(s.unstaffed_hours)}: hours with nobody staffed to them, drawn
          on the hatched track with no tick. They are not counted as hours over — unstaffed is a
          different problem from overloaded.
        </p>`,
      );
    if (this.data.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(this.data.withheld_count)}
          ${this.data.withheld_count === 1 ? 'person is' : 'people are'} not shown: your access
          covers yourself and your reports at the individual grain (the Company row still counts
          them).
        </p>`,
      );
    if (filters.entity && filters.dimension === this.data.dimension) {
      const row = model.rows.find((r) => r.key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${
            row ? `${row.label} is highlighted;` : 'The entity in the filter bar is not a row here;'
          }
          every row stays on the chart so the bars can be compared. Click the name again to clear
          the filter.
        </p>`,
      );
    }
    notes.push(
      html`<p class="note" data-note="scale">
        One scale for every row, so the bars are comparable; the tick moves because capacity
        differs. The bands behind each bar are the run's thresholds applied to that row's own
        capacity, so the colour under a bar means the same thing on every row. The scale is measured
        on the rows alone: the totals beneath are their sum, not another row, and a bar for them
        would flatten every row above it.
      </p>`,
    );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/bullet.js); only the drawing is here.
    return withFormats(bulletColumns({ dimension: this._data?.dimension }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Loading the week…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._controls(model)} ${this._chart(model)} ${this._legend()} ${this._notes(model)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Allocated, high-confidence and capacity hours per ${this._nouns[0].toLowerCase()} — ${this._subtitle(model)}`}
            export-name="bullet-rows"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${bulletExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Bullet rows</div>
            <h2>How many hours over, in the selected week?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${model ? this._kpis(model) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('bullet-rows', BulletRows);
